import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";
import type { TrafficIntent } from "@/types";

const sourceTypeColors: Record<string, string> = {
  domain: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  referrer: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  utm: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  direct: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  social: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400",
};

const buyingStageColors: Record<string, string> = {
  discovery: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400",
  research: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  comparison: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  purchase: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
  support: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400",
};

interface IntentColumnActions {
  onEdit: (intent: TrafficIntent) => void;
  onDelete: (intent: TrafficIntent) => void;
}

export function getIntentColumns(
  actions: IntentColumnActions
): ColumnDef<TrafficIntent>[] {
  return [
    {
      accessorKey: "source_type",
      header: "Source Type",
      cell: ({ row }) => {
        const value = row.getValue("source_type") as string;
        const colorClass = sourceTypeColors[value] ?? sourceTypeColors.direct;
        return (
          <Badge variant="outline" className={colorClass}>
            {value}
          </Badge>
        );
      },
    },
    {
      accessorKey: "source_key",
      header: "Source Key",
      cell: ({ row }) => (
        <span className="max-w-[200px] truncate font-medium">
          {row.getValue("source_key") as string}
        </span>
      ),
    },
    {
      accessorKey: "topic",
      header: "Topic",
      cell: ({ row }) => (
        <span className="max-w-[180px] truncate">{row.getValue("topic") as string}</span>
      ),
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }) => {
        const value = row.getValue("category") as string | null;
        return value ? (
          <span className="text-sm">{value}</span>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        );
      },
    },
    {
      accessorKey: "buying_stage",
      header: "Buying Stage",
      cell: ({ row }) => {
        const value = row.getValue("buying_stage") as string;
        const colorClass = buyingStageColors[value] ?? buyingStageColors.discovery;
        return (
          <Badge variant="outline" className={colorClass}>
            {value}
          </Badge>
        );
      },
    },
    {
      accessorKey: "confidence",
      header: "Confidence",
      cell: ({ row }) => {
        const value = row.getValue("confidence") as number;
        const percentage = Math.round(value * 100);
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-16 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-sm text-muted-foreground">{percentage}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: "channel",
      header: "Channel",
      cell: ({ row }) => {
        const value = row.getValue("channel") as string | null;
        if (!value) return <span className="text-sm text-muted-foreground">--</span>;
        return (
          <span className="text-sm">
            {value.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
          </span>
        );
      },
    },
    {
      accessorKey: "override_by",
      header: "Override By",
      cell: ({ row }) => {
        const value = row.getValue("override_by") as string | null;
        return value ? (
          <Badge variant="secondary">{value}</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">--</span>
        );
      },
    },
    createActionsColumn<TrafficIntent>({ onEdit: actions.onEdit, onDelete: actions.onDelete }),
  ];
}
